import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import debounce from './debounce';
import { BASE_URL } from './utils';
import { clearAuthenticatedQueryCache } from './authenticatedQueryCache';

export const AuthContext = createContext();

export { BASE_URL };
let lastVerificationTime = 0;
const VERIFICATION_COOLDOWN = 5000;
const PUBLIC_ROUTES = ['/', '/login', '/signup', '/enable-2fa', '/help', '/terms', '/privacy', '/forgot-password', '/reset-password', '/verify-email', '/subscribe'];

export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
});

export const AuthProvider = ({ children }) => {
  const [token, setStoredToken] = useState(localStorage.getItem('token') || null);
  const [isTokenVerified, setIsTokenVerified] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(!token);
  const [authServiceUnavailable, setAuthServiceUnavailable] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMounted, setIsMounted] = useState(true);

  const setToken = useCallback((nextToken) => {
    clearAuthenticatedQueryCache(queryClient);
    lastVerificationTime = 0;
    setStoredToken(nextToken);
  }, [queryClient]);

  useEffect(() => {
    return () => setIsMounted(false);
  }, []);

  // Configure axios instance
  useEffect(() => {
    const interceptor = axiosInstance.interceptors.request.use((config) => {
      const currentToken = token || localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });

    return () => {
      axiosInstance.interceptors.request.eject(interceptor);
    };
  }, [token]);

  useEffect(() => {
    const currentToken = token || localStorage.getItem('token');
    if (currentToken) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${currentToken}`;
    } else {
      delete axiosInstance.defaults.headers.common.Authorization;
    }
  }, [token]);

  const verifyAndRefreshTokenImmediate = useCallback(async (force = false) => {
    const now = Date.now();
    const pathname = location?.pathname || '/';
    // Skip verification if not forced and in cooldown period
    if (!force && now - lastVerificationTime < VERIFICATION_COOLDOWN) {
      setIsAuthInitialized(true);
      return isTokenVerified;
    }

    // Skip verification for public routes
    if (PUBLIC_ROUTES.includes(pathname) && !token) {
      if (isMounted) setIsTokenVerified(true);
      setIsAuthInitialized(true);
      return true;
    }

    // Handle missing token
    if (!token) {
      if (isMounted) {
        setIsTokenVerified(false);
        setIsAuthInitialized(true);
        try {
          navigate('/login', { replace: true });
        } catch {
          // Navigation failure leaves the unauthenticated state intact.
        }
      }
      return false;
    }

    try {
      const response = await axiosInstance.get('/api/status');

      if (response.status === 200) {
        lastVerificationTime = now;
        if (isMounted) {
          setAuthServiceUnavailable(false);
          setIsTokenVerified(true);
          setIsAuthInitialized(true);
        }
        return true;
      }
    } catch (error) {
      const transientFailure = error.code === 'ECONNABORTED'
        || !error.response
        || error.response?.status >= 500;
      if (transientFailure) {
        if (isMounted) {
          setAuthServiceUnavailable(true);
          setIsAuthInitialized(true);
        }
        return isTokenVerified;
      }

      // Handle token refresh
      if (error.response?.status === 401) {
        try {
          const refreshResponse = await axios.post(
            `${BASE_URL}/api/token/refresh`,
            {},
            { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
          );

          if (refreshResponse.data?.access_token) {
            const newToken = refreshResponse.data.access_token;
            localStorage.setItem('token', newToken);
            if (isMounted) {
              setAuthServiceUnavailable(false);
              setToken(newToken);
              setIsTokenVerified(true);
              setIsAuthInitialized(true);
            }
            lastVerificationTime = now;
            return true;
          }
        } catch {
          // The invalid session is cleared below without logging token context.
        }
      }

      const errorCode = error.response?.data?.detail?.code;
      const authoritativeFailure = error.response?.status === 401
        || errorCode === 'session_revoked'
        || errorCode === 'account_suspended';
      if (!authoritativeFailure) {
        if (isMounted) {
          setAuthServiceUnavailable(true);
          setIsAuthInitialized(true);
        }
        return isTokenVerified;
      }
    }

    // Clear invalid token
    localStorage.removeItem('token');
    if (isMounted) {
      setToken(null);
      setIsTokenVerified(false);
      setIsAuthInitialized(true);
      try {
        navigate('/login', {
          replace: true,
          state: { sessionExpired: true },
        });
      } catch {
        // Session state is already cleared even if navigation cannot complete.
      }
    }
    return false;
  }, [token, navigate, location.pathname, isTokenVerified, isMounted, setToken]);

  const verifyAndRefreshToken = useCallback(
    (...args) => {
      return new Promise((resolve, reject) => {
        const debounced = debounce(
          async () => {
            try {
              const result = await verifyAndRefreshTokenImmediate(...args);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          },
          1000
        );
        debounced();
      });
    },
    [verifyAndRefreshTokenImmediate]
  );

  useEffect(() => {
    if (token) {
      setIsAuthInitialized(false);
      verifyAndRefreshToken().catch(() => setIsAuthInitialized(true));
    } else if (!PUBLIC_ROUTES.includes(location.pathname)) {
      if (isMounted) {
        setIsTokenVerified(false);
        setIsAuthInitialized(true);
        try {
          navigate('/login', { replace: true });
        } catch {
          // The unauthenticated state remains authoritative.
        }
      }
    } else {
      setIsAuthInitialized(true);
    }
  }, [location.pathname, token, verifyAndRefreshToken, isMounted, navigate]);

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isTokenVerified,
        isAuthInitialized,
        authServiceUnavailable,
        setIsTokenVerified,
        verifyAndRefreshToken,
        verifyAndRefreshTokenImmediate,
        axiosInstance,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const notificationStyles = new Map([
  ['success', { backgroundColor: '#d4edda', color: '#155724', borderColor: '#c3e6cb' }],
  ['error', { backgroundColor: '#f8d7da', color: '#721c24', borderColor: '#f5c6cb' }],
  ['warning', { backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffeeba' }],
]);
